import tokens from '@kluein/design-tokens';
import { Tip } from '@kluein/klue-ui';
import { Menu, Button, Text, Box, Combobox } from '@mantine/core';
import { BuildingsIcon } from '@phosphor-icons/react';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import { useAuth } from 'contexts/auth';
import store from 'store';

import type { RivalType } from 'api/api.types';
import type { RootState, Dispatch } from 'store/store.types';

type ProfileLibraryProps = {
  onProfileSelect: (rivalName: string) => void;
  disabled?: boolean;
};

export function ProfileLibrary({
  onProfileSelect,
  disabled,
}: ProfileLibraryProps) {
  const { t } = useTranslation(['Dashboard', 'Common']);
  const [opened, setOpened] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const dispatch = useDispatch<Dispatch>();
  const { isCompanyLandingPageEnabled } = useAuth();

  const rivalsState = useSelector((state: RootState) => state.rivals);
  const rivalsById = useSelector(store.select.rivals.byId);

  // Memoize rival IDs transformation
  const { rivalIds, isRivalsLoading } = useMemo(
    () => ({
      rivalIds: !rivalsState.isLoading
        ? [...rivalsState.allIds].map((id) => +id)
        : undefined,
      isRivalsLoading: rivalsState.isLoading,
    }),
    [rivalsState.isLoading, rivalsState.allIds],
  );

  // Filter to only show rivals with supported profiles
  const supportedRivals = useMemo(() => {
    if (!rivalIds) return [];
    const rivals = rivalIds
      .map((id) => rivalsById.get(id.toString()))
      .filter(
        (rival): rival is RivalType =>
          Boolean(rival) &&
          !rival?.isTemplate &&
          !rival?.isDraft &&
          Boolean(rival?.supportedRivalId),
      );

    // Sort rivals alphabetically by name
    rivals.sort((a, b) => a.name.localeCompare(b.name));

    return rivals;
  }, [rivalIds, rivalsById]);

  // Filter rivals based on search text
  const filteredRivals = useMemo(() => {
    if (!searchFilter.trim()) return supportedRivals;

    const lowerSearch = searchFilter.toLowerCase();
    return supportedRivals.filter((rival) =>
      rival.name.toLowerCase().includes(lowerSearch),
    );
  }, [supportedRivals, searchFilter]);

  // Fetch rivals if they haven't been loaded yet
  useEffect(() => {
    // Only fetch if we're not loading and we have no rival IDs (meaning rivals haven't been fetched)
    if (!isRivalsLoading && !rivalIds) {
      dispatch.rivals.fetchRivals({});
    }
  }, [isRivalsLoading, rivalIds, dispatch.rivals]);

  const handleRivalClick = (rival: RivalType) => {
    onProfileSelect(rival.name);
    setOpened(false);
    setSearchFilter(''); // Reset search when closing
  };

  // Reset search filter when dropdown closes
  useEffect(() => {
    if (!opened) {
      setSearchFilter('');
    }
  }, [opened]);

  if (!isCompanyLandingPageEnabled) {
    return null;
  }

  // Don't render if rivals are still loading or no supported rivals exist
  if (!rivalIds && supportedRivals.length === 0) {
    return null;
  }

  // Don't render if no supported rivals exist
  if (supportedRivals.length === 0) {
    return null;
  }

  return (
    <div className="profile-library-hide-small">
      <Menu
        shadow="md"
        width={300}
        position="bottom-start"
        withinPortal={true}
        offset={8}
        middlewares={{
          flip: true,
          shift: { padding: 8 },
          size: { padding: 8 },
        }}
        opened={opened}
        onChange={setOpened}
      >
        <Tip
          content={t('Dashboard:askKlue.profileLibrary.tooltip')}
          color="dark"
          variant="small"
        >
          <Menu.Target>
            <Button
              size="xs"
              data-test-id="profile-library-button"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              variant={opened ? 'highlight' : 'light'}
              disabled={disabled}
            >
              <BuildingsIcon size={tokens.iconSize.small} />
            </Button>
          </Menu.Target>
        </Tip>

        <Menu.Dropdown
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Combobox>
            <Combobox.Search
              value={searchFilter}
              onChange={(event) => setSearchFilter(event.currentTarget.value)}
              placeholder={t(
                'Dashboard:askKlue.profileLibrary.searchPlaceholder',
              )}
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              styles={{
                input: {
                  width: '100%',
                },
              }}
            />
          </Combobox>
          <Box
            style={{
              maxHeight: 'min(50vh, 350px)',
              overflowY: 'auto',
            }}
          >
            {filteredRivals.length > 0 ? (
              filteredRivals.map((rival) => (
                <Menu.Item
                  key={rival.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRivalClick(rival);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <Text size="sm" c={tokens.color.neutral[900]}>
                    {rival.name}
                  </Text>
                </Menu.Item>
              ))
            ) : (
              <Box p="md">
                <Text size="sm" c={tokens.color.neutral[600]} ta="center">
                  {t('Dashboard:askKlue.profileLibrary.noRivalsFound')}
                </Text>
              </Box>
            )}
          </Box>
        </Menu.Dropdown>
      </Menu>
    </div>
  );
}

export default ProfileLibrary;
